import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCloud,
  IconHelpCircle,
  IconListDetails,
} from '@tabler/icons-react';
import {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {CloudWatchAlarmState} from 'src/api/ProactApiClient';
import {StatCard} from 'src/design/molecules/StatCard';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useGetCloudWatchAlarmsQuery,
  useGetCloudWatchLogGroupsQuery,
  useGetCloudWatchMetricsQuery,
} from 'src/redux/queries/cloudWatch';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {DEFAULT_METRIC_HOURS} from './cloudWatchDisplay';
import {CloudWatchAlarmsTable} from './components/CloudWatchAlarmsTable';
import {CloudWatchLogsPanel} from './components/CloudWatchLogsPanel';
import {CloudWatchMetricsPanel} from './components/CloudWatchMetricsPanel';

type CloudWatchViewType = 'alarms' | 'metrics' | 'logs';

export const CloudWatchPage = () => {
  useSetDocumentTitle('CloudWatch');

  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') ?? 'alarms') as CloudWatchViewType;

  const [hours, setHours] = useState(DEFAULT_METRIC_HOURS);
  const [stateFilter, setStateFilter] = useState<
    CloudWatchAlarmState | undefined
  >(undefined);

  const {
    data: alarmsData,
    isLoading: alarmsLoading,
    isFetching: alarmsFetching,
    isError: alarmsIsError,
    error: alarmsError,
    refetch: refetchAlarms,
  } = useGetCloudWatchAlarmsQuery();
  // Metrics costs a DescribeAlarms + GetMetricData round trip, so it only runs on its own tab.
  const metricsActive = view === 'metrics';
  const {
    data: metricsData,
    isLoading: metricsLoading,
    isUninitialized: metricsUninitialized,
    isFetching: metricsFetching,
    isError: metricsIsError,
    error: metricsError,
    refetch: refetchMetrics,
  } = useGetCloudWatchMetricsQuery(hours, {skip: !metricsActive});
  const logsActive = view === 'logs';
  const {
    data: logGroups,
    isLoading: groupsLoading,
    isUninitialized: groupsUninitialized,
    isError: groupsIsError,
    error: groupsError,
    refetch: refetchGroups,
  } = useGetCloudWatchLogGroupsQuery(undefined, {skip: !logsActive});

  const environment = alarmsData?.environment ?? '';

  const {alarms, counts} = useMemo(() => {
    const list = alarmsData?.alarms ?? [];
    return {
      alarms: list,
      counts: {
        alarm: list.filter(a => a.state === CloudWatchAlarmState.Alarm).length,
        ok: list.filter(a => a.state === CloudWatchAlarmState.Ok).length,
        insufficient: list.filter(
          a => a.state === CloudWatchAlarmState.InsufficientData
        ).length,
        total: list.length,
      },
    };
  }, [alarmsData]);

  const selectView = (next: string): void => {
    setSearchParams(params => {
      const updated = new URLSearchParams(params);
      updated.set('view', next);
      return updated;
    });
  };

  const filterByState = (state?: CloudWatchAlarmState): void => {
    setStateFilter(current => (current === state ? undefined : state));
    selectView('alarms');
  };

  const refreshAll = (): void => {
    void refetchAlarms();
    if (metricsActive) void refetchMetrics();
    if (logsActive) void refetchGroups();
  };

  const {RenderedTabsComponent} = useUrlQueryTabV2<CloudWatchViewType>({
    view,
    defaultView: 'alarms',
    onSelectView: selectView,
    tabs: [
      {
        view: 'alarms',
        label: 'Alarms',
        component: (
          <CloudWatchAlarmsTable
            alarms={alarms}
            isLoading={alarmsLoading}
            isFetching={alarmsFetching}
            isError={alarmsIsError}
            error={alarmsIsError ? parseErrorMessage(alarmsError) : undefined}
            stateFilter={stateFilter}
            onRefresh={refetchAlarms}
          />
        ),
      },
      {
        view: 'metrics',
        label: 'Metrics',
        component: (
          <CloudWatchMetricsPanel
            series={metricsData?.series ?? []}
            hours={hours}
            onHoursChange={setHours}
            isLoading={metricsLoading || metricsUninitialized}
            isRefreshing={metricsFetching && !metricsLoading}
            isError={metricsIsError}
            errorMessage={
              metricsIsError ? parseErrorMessage(metricsError) : undefined
            }
          />
        ),
      },
      {
        view: 'logs',
        label: 'Logs',
        component: (
          <CloudWatchLogsPanel
            groups={logGroups ?? []}
            groupsLoading={groupsLoading || groupsUninitialized}
            groupsError={groupsIsError}
            groupsErrorMessage={
              groupsIsError ? parseErrorMessage(groupsError) : undefined
            }
          />
        ),
      },
    ],
  });

  return (
    <ContentWrapper>
      <Stack gap="lg">
        <BackBar
          hideBackButton
          breadcrumbs={[{label: 'Admin'}, {label: 'CloudWatch'}]}
        />

        <PageHeader>
          <PageHeader.Title
            icon={<IconCloud size={IconSize.md} />}
            subtitle={
              environment
                ? `Alarms, metrics, and ECS application logs for the ${environment} environment`
                : 'Alarms, metrics, and ECS application logs pulled live from CloudWatch'
            }>
            CloudWatch
          </PageHeader.Title>
          <PageHeader.Actions>
            <RefreshButton
              size="sm"
              onClick={refreshAll}
              loading={alarmsFetching || (metricsActive && metricsFetching)}
            />
          </PageHeader.Actions>
        </PageHeader>

        {alarmsIsError && (
          <ErrorAlert message={parseErrorMessage(alarmsError)} />
        )}

        <SimpleGrid cols={{base: 2, md: 4}} spacing="md">
          <StatCard
            title="In alarm"
            count={counts.alarm}
            icon={<IconAlertTriangle size={IconSize.xl} />}
            color="red"
            isActive={stateFilter === CloudWatchAlarmState.Alarm}
            onClick={() => filterByState(CloudWatchAlarmState.Alarm)}
            tooltip="Show only alarms in the ALARM state"
          />
          <StatCard
            title="OK"
            count={counts.ok}
            icon={<IconCircleCheck size={IconSize.xl} />}
            color="green"
            isActive={stateFilter === CloudWatchAlarmState.Ok}
            onClick={() => filterByState(CloudWatchAlarmState.Ok)}
            tooltip="Show only alarms in the OK state"
          />
          <StatCard
            title="Insufficient data"
            count={counts.insufficient}
            icon={<IconHelpCircle size={IconSize.xl} />}
            color="gray"
            isActive={stateFilter === CloudWatchAlarmState.InsufficientData}
            onClick={() => filterByState(CloudWatchAlarmState.InsufficientData)}
            tooltip="Show only alarms with insufficient data"
          />
          <StatCard
            title="Alarms total"
            count={counts.total}
            icon={<IconListDetails size={IconSize.xl} />}
            color="blue"
            isActive={stateFilter === undefined}
            onClick={() => filterByState(undefined)}
            tooltip="Show every alarm"
          />
        </SimpleGrid>

        {RenderedTabsComponent}
      </Stack>
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.AdminCloudWatch(),
  element: <CloudWatchPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
